import {useRouter} from 'next/router'
import {ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'

import {
    CONNECTION_ERROR_CODE_LIST,
    MyDataUpdater,
    SYSTEM_MAINTENANCE_ERROR_CODE_LIST,
    SessionIO,
    TEMPORARY_ERROR_CODE_LIST,
    UP_TO_DATE_CODE_LIST,
    UpdateErrorInfo,
} from '@mydata/updater'
import {Cookies} from 'react-cookie'

import {useMemberContext} from '$contexts/member'
import {getConnectionInfo} from '$services/sessionIo'
import {requestUpdateAsync} from '$services/updater'
import {isLocalProfile, isRealProfile} from '$share/utils/env'
import {UserInfo} from '$types/member'
import buildUrl from '$utils/buildUrl'

const cookies = new Cookies()
export const MYDATA_UPDATE_LOGGING_ENABLED = 'MYDATA_UPDATE_LOGGING_ENABLED'

type RequestUpdateKey = string

interface ErrorListType {
    shutdownErrorList: UpdateErrorInfo[]
    maintenanceErrorList: UpdateErrorInfo[]
    connectionErrorList: UpdateErrorInfo[]
    temporaryErrorList: UpdateErrorInfo[]
}

interface UpdaterContextType {
    myDataUpdater: MyDataUpdater
    errorList: ErrorListType
}
const Context = createContext<UpdaterContextType | undefined>(undefined)

export function UpdaterProvider({userInfo, children}: {userInfo?: UserInfo; children: ReactNode}) {
    const {isSuccess, info} = useMemberContext()
    const router = useRouter()
    const [errorMap, setErrorMap] = useState<{[key in RequestUpdateKey]: UpdateErrorInfo}>({})

    const myDataSessionIo = useMemo(() => {
        return new SessionIO(getConnectionInfo, {
            debug: !isLocalProfile && !isRealProfile && cookies.get(MYDATA_UPDATE_LOGGING_ENABLED),
        })
    }, [])

    const removeError = useCallback((requestUpdateKey: RequestUpdateKey) => {
        setErrorMap((prev) => {
            const filteredList = Object.entries(prev).filter(([key]) => key !== requestUpdateKey)
            return Object.fromEntries(filteredList)
        })
    }, [])

    const addError = useCallback((requestUpdateKey: RequestUpdateKey, updateErrorInfo: UpdateErrorInfo) => {
        // 00001 무시
        if (!UP_TO_DATE_CODE_LIST.includes(updateErrorInfo.code)) {
            setErrorMap((prev) => ({...prev, [requestUpdateKey]: updateErrorInfo}))
        }
    }, [])

    const myDataUpdater = useMemo(
        () =>
            new MyDataUpdater(myDataSessionIo, {
                handleError: {removeError, addError},
                fetch: {
                    updateRequest: requestUpdateAsync,
                },
            }),
        [addError, myDataSessionIo, removeError],
    )

    const errorList = useMemo(() => {
        const currentPathName = new URL(buildUrl({pathname: router.pathname, query: router.query, withBasePath: true}))
            .pathname

        const errors = Object.values(errorMap).filter((updateError) => {
            return updateError.pathname === currentPathName
        })

        return {
            shutdownErrorList: errors.filter(({code}) => code === 'SHUTDOWN'),
            maintenanceErrorList: errors.filter(({code}) => SYSTEM_MAINTENANCE_ERROR_CODE_LIST.includes(code)),
            connectionErrorList: errors.filter(({code}) => CONNECTION_ERROR_CODE_LIST.includes(code)),
            temporaryErrorList: errors.filter(({code}) => TEMPORARY_ERROR_CODE_LIST.includes(code)),
        }
    }, [errorMap, router.pathname, router.query])

    const value = useMemo(() => ({myDataUpdater, errorList}), [myDataUpdater, errorList])

    useEffect(() => {
        if (isSuccess(info)) {
            myDataSessionIo.setNaverId(info.naverId)
            return
        }

        if (userInfo?.naverId) {
            myDataSessionIo.setNaverId(userInfo.naverId)
        }
    }, [info, isSuccess, myDataSessionIo, userInfo?.naverId])

    return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useUpdaterContext() {
    const context = useContext(Context)

    if (context === undefined) {
        throw new Error('useUpdaterContext must be within UpdaterProvider')
    }

    return context
}
